import { css } from "@emotion/react";
import { useCallback, useState } from "react";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import React from "react";
import { Checkbox } from "../Checkbox";
import { ListItem, ListItemIcon } from "../List";
import { MenuList } from "../Menu";
import { Popper } from "../Popper";
import { resetButtonStyle } from "../Button";
import { useTheme } from "../theme";
import { WithPagination, WithPaginationProps } from "./WithPagination";
import { useGridLayout } from "./useGridLayout";
import { useSort } from "./useSort";
import type { CSSObject, SerializedStyles } from "@emotion/react";
import type { Theme } from "../theme";

export interface TableUIRenderCellProps<Row, Value> {
  cell: { value: Value };
  row: Row;
}

const DefaultRenderCell = (
  props: TableUIRenderCellProps<Record<string, unknown>, unknown>,
) => {
  const { cell } = props;
  return (
    <div>
      {typeof cell.value === "string" ? cell.value : JSON.stringify(cell.value)}
    </div>
  );
};

const tableCellStyle = css`
  box-sizing: border-box;
  padding: 8px 16px;
  font: inherit;
  line-height: inherit;
  text-align: left;
  letter-spacing: inherit;
  border-bottom: 1px solid #e0e0e0;
  &:first-of-type:has(input[type="checkbox"]) {
    width: 56px;
    padding: 0px;
    text-align: center;
    label {
      display: inline-flex;
      padding: 8px;
    }
  }
`;

interface HeaderCellProps extends React.PropsWithChildren {
  menuItems: {
    label: string;
    onClick: () => void;
    renderIcon: (props: Record<string, unknown>) => React.JSX.Element;
  }[];
}

const HeaderCell = (props: HeaderCellProps) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement>();
  const handleClose = useCallback(() => {
    setAnchorEl(undefined);
  }, []);
  const handleToggle = useCallback(
    (event: React.SyntheticEvent<HTMLElement>) => {
      const { currentTarget } = event;
      setAnchorEl((prev) => (prev ? undefined : currentTarget));
    },
    [],
  );
  const open = Boolean(anchorEl);

  return (
    <th
      css={[
        tableCellStyle,
        css`
          padding: 0px;
          cursor: pointer;
          background-color: white;
          transition: background-color 0.1s ease-in-out;
          &:hover {
            background-color: ${theme.palette.gray[200]};
          }
        `,
      ]}
    >
      <button
        css={[
          resetButtonStyle,
          css`
            display: inline-block;
            width: 100%;
            padding: 8px 16px;
            text-align: inherit;
          `,
        ]}
        onClick={handleToggle}
      >
        {props.children}
      </button>
      <Popper
        disablePortal
        onClickOutside={handleClose}
        anchorEl={anchorEl}
        open={open}
        placement="bottom-start"
      >
        <MenuList
          paperProps={{
            elevation: 5,
          }}
        >
          {props.menuItems.map(({ onClick, label, renderIcon: Icon }) => {
            return (
              <ListItem
                key={label}
                onClick={() => {
                  onClick();
                  handleClose();
                }}
              >
                <ListItemIcon>
                  <Icon />
                </ListItemIcon>
                {label}
              </ListItem>
            );
          })}
        </MenuList>
      </Popper>
    </th>
  );
};

export type TableUIColumn<Row> = {
  [Key in keyof Row]: {
    // このキーのユニークチェックを行った方が良い。
    // 出来ればランタイムは無駄が大きいので型でチェックしたい。
    key: Key;
    title?: string;
    renderCell?: ({
      cell,
      row,
    }: TableUIRenderCellProps<Row, Row[Key]>) => React.JSX.Element;
    // デフォルトでソートは有効化する
    sort?: (a: Row[Key], b: Row[Key]) => number;
    /**
     * この列の幅が全体に占める比を指定する。
     * columnRatio指定しない場合は、autoが指定されたものとして扱われます。
     * table要素本来の自動調整が効かなくなるので、注意して利用してください。
     */
    columnRatio?: number;
  };
}[keyof Row];

export interface TableUIPropsOnSelectArgs<Row> {
  row: Row;
  index: number;
  checked: boolean;
}

export interface TableUIPropsRenderActionsArgs<Row> {
  row: Row;
  index: number;
}

export interface TableUIPropsGetRowStyleArgs<Row> {
  row: Row;
  index: number;
  theme: Theme;
}

export interface TableUIProps<Row> extends WithPaginationProps {
  /**
   * テーブルをストライプ柄にするかどうか
   * @default false
   */
  striped?: boolean;
  /**
   * テーブルのヘッダーを固定するかどうか
   * @default false
   */
  stickyHeader?: boolean;
  /**
   * テーブルの列の情報
   */
  columns?: TableUIColumn<Row>[];
  /**
   * 空配列の場合は読み込み済み、undefinedの場合は読み込み中であると見なされます。
   * 基本的にJSONフレンドリーなオブジェクトを渡すようにしてください。ReactElementなどは非推奨です。
   * 代わりにcolumnsで定義できるrenderCellを利用するか、ReactComponentを渡してください。
   *
   * dataにはcolumnsに存在しないものも含められます。
   */
  data?: Row[];
  /**
   * 現在選択されている行のindexの配列
   */
  selectedIndex?: number[];
  /**
   * 行毎のチェックボックスが生える
   */
  onSelect?: ({ row, index, checked }: TableUIPropsOnSelectArgs<Row>) => void;
  /**
   * 全てをチェックするボタンが生える
   */
  onSelectAll?: ({ data, checked }: { data: Row[]; checked: boolean }) => void;
  /**
   * onSelectで生えるチェックボックスのname
   * formDataから取得できる値は選択された行のindexです。
   */
  name?: string;
  /**
   * 行の末尾にインタラクティブ要素を追加するためのメソッド
   */
  renderActions?: (
    args: TableUIPropsRenderActionsArgs<Row>,
  ) => React.JSX.Element;
  /**
   * 行のスタイルをカスタマイズするための関数
   * @param args - 行のデータ、インデックス、テーマオブジェクトを含むオブジェクト
   * @returns Emotion CSSオブジェクトまたはCSSオブジェクト
   */
  getRowStyle?: (
    args: TableUIPropsGetRowStyleArgs<Row>,
  ) => SerializedStyles | CSSObject | undefined;
}

export const TableUI = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Row extends Record<string, any>,
>(
  props: TableUIProps<Row>,
) => {
  const {
    columns,
    data,
    striped,
    stickyHeader,
    onSelect,
    selectedIndex = [],
    onSelectAll,
    name,
    renderActions: RenderActions,
    onPaginate,
    pageTotal,
    getRowStyle,
  } = props;
  const theme = useTheme();

  const allChecked = data?.length === selectedIndex?.length;

  const [dataSorted, order, setOrder] = useSort(columns, data);

  const gridStyle = useGridLayout(columns, {
    hasSelectColumn: Boolean(onSelect),
    hasActionsColumn: Boolean(RenderActions),
  });

  return (
    <WithPagination onPaginate={onPaginate} pageTotal={pageTotal}>
      <table
        css={[
          theme.typography.body,
          css`
            box-sizing: border-box;
            width: 100%;
            border-spacing: 0;
            border-collapse: separate;
            isolation: isolate;
            border-top: 1px solid #e0e0e0;
            border-right: 2px solid #e0e0e0;
            border-bottom: 1px solid #e0e0e0;
            border-left: 2px solid #e0e0e0;
            thead {
              box-sizing: border-box;
              th {
                white-space: nowrap;
                border-top: 1px solid #e0e0e0;
                border-bottom: 2px solid #e0e0e0;
              }
            }
          `,
          striped
            ? css`
                tr:nth-of-type(even) > td {
                  background-color: #f2f2f2;
                }
              `
            : null,
          stickyHeader
            ? css`
                position: relative;
                /**
                 * ここでtheadをstickyにすると、grid layoutのサポートが困難になる。
                 */
                thead tr th {
                  position: sticky;
                  top: 0px;
                  z-index: ${theme.zIndex.appBar};
                }
              `
            : null,
          gridStyle,
        ]}
      >
        <thead>
          <tr>
            {onSelect ? (
              <th
                css={[
                  tableCellStyle,
                  css`
                    background-color: white;
                  `,
                ]}
              >
                {onSelectAll ? (
                  // eslint-disable-next-line jsx-a11y/label-has-associated-control
                  <label>
                    <Checkbox
                      defaultChecked={allChecked}
                      key={allChecked?.toString()}
                      onChange={(event) => {
                        if (data) {
                          onSelectAll({ data, checked: event.target.checked });
                        }
                      }}
                      indeterminate={selectedIndex.length > 0 && !allChecked}
                    />
                  </label>
                ) : null}
              </th>
            ) : null}
            {columns?.map((column) => {
              const orderBy = column.key.toString();
              return (
                <HeaderCell
                  menuItems={[
                    {
                      label: "昇順で並び替え",
                      //@ts-expect-error muiのiconの方がmuiNameプロパティを型で持つようになったためエラーが出る
                      renderIcon: ArrowUpwardIcon,
                      onClick: () => {
                        setOrder({ by: orderBy, direction: "asc" });
                      },
                    },
                    {
                      label: "降順で並び替え",
                      //@ts-expect-error muiのiconの方がmuiNameプロパティを型で持つようになったためエラーが出る
                      renderIcon: ArrowDownwardIcon,
                      onClick: () => {
                        setOrder({ by: orderBy, direction: "desc" });
                      },
                    },
                  ]}
                  key={column.key.toString()}
                >
                  <div
                    css={css`
                      position: relative;
                    `}
                  >
                    {column.title}
                    <span
                      css={css`
                        position: absolute;
                        display: inline-flex;
                        margin-left: 4px;
                        color: ${theme.palette.text.hint};
                      `}
                    >
                      {order?.by === orderBy ? (
                        order.direction === "desc" ? (
                          <ArrowDownwardIcon />
                        ) : (
                          <ArrowUpwardIcon />
                        )
                      ) : null}
                    </span>
                  </div>
                </HeaderCell>
              );
            })}
            {RenderActions ? (
              <th
                css={[
                  tableCellStyle,
                  css`
                    background-color: white;
                  `,
                ]}
              />
            ) : null}
          </tr>
        </thead>
        <tbody>
          {dataSorted?.map(({ row, index }) => {
            const checked = selectedIndex?.includes(index);
            const customRowStyle = getRowStyle?.({ row, index, theme });
            return (
              <tr
                key={`${index}`}
                css={[
                  css`
                    &:has(input[type="checkbox"]:checked) > td {
                      background-color: ${theme.palette.intentions.primary
                        .light};
                    }
                  `,
                  customRowStyle,
                ]}
              >
                {onSelect ? (
                  <td css={tableCellStyle}>
                    {
                      // eslint-disable-next-line jsx-a11y/label-has-associated-control
                      <label>
                        <Checkbox
                          key={checked?.toString()}
                          name={name}
                          value={index}
                          defaultChecked={checked}
                          onChange={(event) => {
                            onSelect({
                              row,
                              index,
                              checked: event.target.checked,
                            });
                          }}
                        />
                      </label>
                    }
                  </td>
                ) : null}
                {columns?.map((column) => {
                  const cell = row[column.key];
                  const { renderCell: RenderCell = DefaultRenderCell } = column;
                  return (
                    <td key={column.key.toString()} css={tableCellStyle}>
                      <RenderCell cell={{ value: cell }} row={row} />
                    </td>
                  );
                })}
                {RenderActions ? (
                  <td css={tableCellStyle}>
                    <RenderActions row={row} index={index} />
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </WithPagination>
  );
};
